import { AutocompleteInput } from "@/components/ui/AutocompleteInput";
import { Select } from "@/components/ui/Select";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2, Calendar, FileText, Stethoscope, Tag, X } from "lucide-react";
import React from "react";

import { DateInput } from "../../ui/DateInput";
import { VisitCitationRowsEditor } from "./VisitCitationRowsEditor";
import { NEW_VISIT_CITATIONS_ERROR_KEY } from "./Visits.types";
import type {
  CaseFileOption,
  MedChronTemplate,
  NewVisitValues,
  VisitFieldSuggestions,
} from "./types";
import { buildPageLimits, validateCitationDraftRows } from "./utils/visitCitations";
import { buildFileOptions, buildVisitTypeOptions } from "./utils/visitFormatting";

interface NewVisitFormModalProps {
  newVisitValues: NewVisitValues;
  setNewVisitValues: React.Dispatch<React.SetStateAction<NewVisitValues>>;
  formErrors: string[];
  setFormErrors: React.Dispatch<React.SetStateAction<string[]>>;
  template: MedChronTemplate | null;
  availableFiles: CaseFileOption[];
  fileMapping: Record<string, string>;
  fieldSuggestions: VisitFieldSuggestions;
  onAddVisit: () => void;
  onClose: () => void;
}

export const NewVisitFormModal: React.FC<NewVisitFormModalProps> = ({
  newVisitValues,
  setNewVisitValues,
  formErrors,
  setFormErrors,
  template,
  availableFiles,
  fileMapping,
  fieldSuggestions,
  onAddVisit,
  onClose,
}) => {
  const hasError = (field: string) => formErrors.includes(field);
  const showCitationErrors = hasError(NEW_VISIT_CITATIONS_ERROR_KEY);
  const { rowErrors: citationRowErrors } = validateCitationDraftRows(
    newVisitValues.citations,
    new Set(availableFiles.map((file) => file.file_id)),
    buildPageLimits(availableFiles)
  );

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        size="lg"
        hideCloseButton
        className="flex max-h-[95vh] flex-col gap-0 overflow-hidden p-0"
      >
        {/* Header */}
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between space-y-0 border-b border-neutral-100 px-6 py-4 text-left">
          <div>
            <DialogTitle className="text-xl font-bold text-neutral-900">Add New Visit</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-neutral-500">
              Enter the medical encounter details below.
            </DialogDescription>
          </div>
          <DialogClose
            type="button"
            className="rounded-full p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            aria-label="Close modal"
          >
            <X size={20} />
          </DialogClose>
        </DialogHeader>

        {/* Form Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Date Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="visit_date"
                className="flex items-center gap-2 text-sm font-semibold text-neutral-700"
              >
                <Calendar size={16} className="text-primary" />
                Visit Date
              </label>
              <DateInput
                id="visit_date"
                value={newVisitValues.visit_date}
                onChange={(val) => {
                  setNewVisitValues((v) => ({ ...v, visit_date: val }));
                  setFormErrors((prev) => prev.filter((f) => f !== "Visit date"));
                }}
                className={`w-full cursor-pointer rounded-lg border px-3 py-2.5 pr-10 text-sm transition-all ${
                  hasError("Visit date")
                    ? "border-destructive bg-destructive-subtle focus-visible:shadow-focus-danger"
                    : "border-neutral-300 focus-visible:shadow-focus"
                }`}
              />
              {hasError("Visit date") && (
                <p className="text-xs text-destructive-text">Visit date is required</p>
              )}
            </div>

            {/* Visit Type Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="visit_type"
                className="flex items-center gap-2 text-sm font-semibold text-neutral-700"
              >
                <Stethoscope size={16} className="text-primary" />
                Visit Type
              </label>
              <Select
                id="visit_type"
                value={newVisitValues.visit_type_id}
                onChange={(next) => {
                  setNewVisitValues((v) => ({ ...v, visit_type_id: next }));
                  setFormErrors((prev) => prev.filter((f) => f !== "Visit type"));
                }}
                options={buildVisitTypeOptions(template)}
                placeholder="Select visit type"
                className={`w-full rounded-lg border px-3 py-2.5 text-sm transition-all ${
                  hasError("Visit type")
                    ? "border-destructive bg-destructive-subtle focus-visible:shadow-focus-danger"
                    : "border-neutral-300 focus-visible:shadow-focus"
                }`}
              />
              {hasError("Visit type") && (
                <p className="text-xs text-destructive-text">Please select a visit type</p>
              )}
            </div>

            {/* Subtype Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="subtype"
                className="flex items-center gap-2 text-sm font-semibold text-neutral-700"
              >
                <Tag size={16} className="text-primary" />
                Subtype
              </label>
              <AutocompleteInput
                id="subtype"
                placeholder="Progress Notes"
                value={newVisitValues.visit_subtype}
                suggestions={fieldSuggestions.subtypes}
                onChange={(next) => {
                  setNewVisitValues((v) => ({ ...v, visit_subtype: next }));
                }}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm transition-all focus-visible:shadow-focus"
              />
            </div>

            {/* Provider Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="provider"
                className="flex items-center gap-2 text-sm font-semibold text-neutral-700"
              >
                <Stethoscope size={16} className="text-primary" />
                Provider
              </label>
              <AutocompleteInput
                id="provider"
                placeholder="Dr. Smith"
                value={newVisitValues.provider_name}
                suggestions={fieldSuggestions.providers}
                onChange={(next) => {
                  setNewVisitValues((v) => ({ ...v, provider_name: next }));
                  setFormErrors((prev) => prev.filter((f) => f !== "Provider name"));
                }}
                className={`w-full rounded-lg border px-3 py-2.5 text-sm transition-all ${
                  hasError("Provider name")
                    ? "border-destructive bg-destructive-subtle focus-visible:shadow-focus-danger"
                    : "border-neutral-300 focus-visible:shadow-focus"
                }`}
              />
              {hasError("Provider name") && (
                <p className="text-xs text-destructive-text">Provider name is required</p>
              )}
            </div>

            {/* Facility Field */}
            <div className="space-y-1.5">
              <label
                htmlFor="facility"
                className="flex items-center gap-2 text-sm font-semibold text-neutral-700"
              >
                <Building2 size={16} className="text-primary" />
                Facility
              </label>
              <AutocompleteInput
                id="facility"
                placeholder="General Hospital"
                value={newVisitValues.facility_name}
                suggestions={fieldSuggestions.facilities}
                onChange={(next) => {
                  setNewVisitValues((v) => ({ ...v, facility_name: next }));
                  setFormErrors((prev) => prev.filter((f) => f !== "Facility name"));
                }}
                className={`w-full rounded-lg border px-3 py-2.5 text-sm transition-all ${
                  hasError("Facility name")
                    ? "border-destructive bg-destructive-subtle focus-visible:shadow-focus-danger"
                    : "border-neutral-300 focus-visible:shadow-focus"
                }`}
              />
              {hasError("Facility name") && (
                <p className="text-xs text-destructive-text">Facility name is required</p>
              )}
            </div>

            <div
              role="group"
              aria-labelledby="visit-citations-label"
              className="space-y-1.5 md:col-span-2"
            >
              <p
                id="visit-citations-label"
                className="flex items-center gap-2 text-sm font-semibold text-neutral-700"
              >
                <FileText size={16} className="text-primary" />
                Citations
              </p>
              <VisitCitationRowsEditor
                rows={newVisitValues.citations}
                rowErrors={showCitationErrors ? citationRowErrors : []}
                fileOptions={buildFileOptions(availableFiles, fileMapping)}
                onChange={(citations) => {
                  setNewVisitValues((v) => ({ ...v, citations }));
                  setFormErrors((prev) => prev.filter((f) => f !== NEW_VISIT_CITATIONS_ERROR_KEY));
                }}
              />
              {showCitationErrors && newVisitValues.citations.length === 0 && (
                <p className="text-xs text-destructive-text">Add at least one citation</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-neutral-100 bg-neutral-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-neutral-300 bg-card px-5 py-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 active:bg-neutral-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onAddVisit}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover hover:shadow-md active:scale-[0.98]"
          >
            Add Visit
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
